/*
  Predictive search, following the ARIA combobox pattern.

  The input owns focus the whole time. Arrow keys move aria-activedescendant
  through the results listbox, Enter follows the active option, Escape closes
  the results first and the dialog second. Results are rendered by Liquid
  (sections/predictive-search.liquid) through the Section Rendering API, so
  prices, currency and card markup match the rest of the store.
*/
import { getRoutes, fetchDocument, debounce, announce } from '@theme/utils';

const MIN_LENGTH = 2;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[role="combobox"]');
    this.container = this.querySelector('[data-predictive-container]');
    if (!this.input || !this.container) return;

    this.options = [];
    this.activeIndex = -1;
    this.abort = null;
    this.lookup = debounce((query) => this.fetchResults(query), 250);

    this.input.addEventListener('input', () => {
      // Never act on a suggestion that belongs to an earlier query.
      this.setActive(-1);
      const query = this.input.value.trim();
      if (query.length < MIN_LENGTH) {
        this.lookup.cancel();
        this.close();
        return;
      }
      this.lookup(query);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));

    // Reset when the surrounding dialog closes.
    this.closest('dialog')?.addEventListener('close', () => {
      this.close();
      this.input.value = '';
    });
  }

  get isOpen() {
    return this.input.getAttribute('aria-expanded') === 'true';
  }

  async fetchResults(query) {
    this.abort?.abort();
    this.abort = new AbortController();
    const params = new URLSearchParams({
      q: query,
      section_id: this.dataset.sectionId,
      'resources[type]': 'product,collection,page,article,query',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
    });
    try {
      const doc = await fetchDocument(`${getRoutes().predictiveSearchUrl}?${params}`, { signal: this.abort.signal });
      // Discard a response that no longer matches what the visitor has typed.
      if (query !== this.input.value.trim()) return;
      const results = doc.querySelector('[data-predictive-results]');
      if (!results) return this.close();
      this.container.innerHTML = results.innerHTML;
      this.options = [...this.container.querySelectorAll('[role="option"]')];
      this.activeIndex = -1;
      this.open();
      announce(results.dataset.status);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        this.close();
      }
    }
  }

  open() {
    this.container.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.abort?.abort();
    this.container.hidden = true;
    this.container.innerHTML = '';
    this.options = [];
    this.activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  setActive(index) {
    this.options.forEach((option, position) => option.setAttribute('aria-selected', String(position === index)));
    this.activeIndex = index;
    const active = this.options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.options.length) return;
      event.preventDefault();
      if (!this.isOpen) this.open();
      const last = this.options.length - 1;
      const next = event.key === 'ArrowDown' ? (this.activeIndex >= last ? 0 : this.activeIndex + 1) : this.activeIndex <= 0 ? last : this.activeIndex - 1;
      this.setActive(next);
    } else if (event.key === 'Enter' && this.activeIndex > -1) {
      event.preventDefault();
      this.options[this.activeIndex].querySelector('a')?.click();
    } else if (event.key === 'Escape' && this.isOpen) {
      // Close the results first. Stop the native "clear field" and dialog close.
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
